import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpEvent,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Observable,  BehaviorSubject, throwError } from 'rxjs';
import { filter, catchError, switchMap, finalize, take  } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { HttpStatusCodes } from '@zerops/zef/core';
import { ZefAuthApi } from './auth.api';
import { AuthTokenStorageService } from './auth.utils';
import { TokenData } from './auth.model';
import { authActions } from './auth.state';
import { API_REFRESH_ENDPOINT } from './auth.constant';

@Injectable()
export class AuthAndRefreshTokenInterceptor implements HttpInterceptor {

  /**
   * Dependencies.
   */
  #api = inject(ZefAuthApi);
  #store = inject(Store);
  #tokenService = inject(AuthTokenStorageService);
  #apiRefreshEndpoint = inject(API_REFRESH_ENDPOINT);

  /**
   * Private class members.
   */
  #isRefreshingToken = false;
  #activeRequests = 0;
  #tokenSubject$ = new BehaviorSubject<string>(undefined);
  #refreshFailed = false;
  #refreshFailedForToken: string | null = null;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      const _tokenData = this.#tokenService.getToken();

      return next.handle(this.#addToken(
        req,
        _tokenData ? _tokenData.accessToken : undefined
      )).pipe(
        catchError((err: HttpErrorResponse) => {
          /**
           * The refresh endpoint must never trigger refresh-on-401. The refresh call goes through this same interceptor,
           * so a 401 on it would re-enter the logic below with #isRefreshingToken already true, fall into the wait branch
           * and deadlock on #tokenSubject$ (which only the outer refresh pipeline can resolve).
           * The result: refreshTokenFail never fires, so neither @zerops/zef/auth nor @zerops/zerops/user-data is ever
           * cleared through clearUserData(). Rethrow immediately so the outer refresh$ pipeline handles the failure.
           */
          if (req.url === this.#apiRefreshEndpoint) {
            return throwError(() => err);
          }

          /**
           * Add early exit if refresh has already failed for this token.
           */
          if (err.status === HttpStatusCodes.UNAUTHORIZED && this.#refreshFailed) {
            /**
             * Allow retry if token changed (user re-logged in).
             */
            if (_tokenData && _tokenData.accessToken !== this.#refreshFailedForToken) {
              this.#refreshFailed = false;
              this.#refreshFailedForToken = null;
            } else {
              return throwError(() => err);
            }
          }

          if (err.status === HttpStatusCodes.UNAUTHORIZED) {
            /**
             * Skip refresh when another tab invalidated the session - the modal is showing and we'll hard-reload on user action.
             */
            if (this.#tokenService.sessionInvalidated()) {
              return throwError(() => err);
            }

            /**
             * Only attempt refresh if we have token data with a refresh token. Without this check, login failures (401)
             * would crash trying to access undefined.refreshToken.
             */
            if (!_tokenData || !_tokenData.refreshToken) {
              return throwError(() => err);
            }

            this.#activeRequests = this.#activeRequests + 1;

            /**
             * Check if token was already refreshed by another tab or request.
             */
            const currentToken = this.#tokenService.getToken();
            if (currentToken && currentToken.accessToken !== _tokenData.accessToken) {
              this.#activeRequests = this.#activeRequests - 1;
              return next.handle(this.#addToken(req, currentToken.accessToken));
            }

            if (!this.#isRefreshingToken) {
              this.#isRefreshingToken = true;
              this.#refreshFailed = false;
              this.#refreshFailedForToken = null;

              /**
               * A failed token refresh means the session is over — refreshTokenFail
               * below already redirects to /login. Surface it as a brief red toast
               * ('snack'), not the full "Couldn't load vital parts / our servers are
               * down" dialog, which is inaccurate: nothing is down, the refresh token
               * just expired.
               */
              const refreshTokenAction = authActions.refreshToken(undefined, {
                type: 'snack'
              });

              this.#store.dispatch(refreshTokenAction);

              /**
               * Reset here so that the following requests wait until the token comes back from the refreshToken call.
               */
              this.#tokenSubject$.next(undefined);

              return this.#api
                .refresh$<TokenData>(_tokenData.refreshToken)
                .pipe(
                  /**
                   * Catch refresh API errors before switchMap so retried-request errors don't incorrectly dispatch refreshTokenFail.
                   */
                  catchError((innerErr: HttpErrorResponse) => {
                    /**
                     * Session invalidated while refresh was in-flight — silently clean up interceptor state without dispatching refreshTokenFail.
                     * The cross-tab dialog handles all cleanup; dispatching auth-clear actions would cascade into clearUserData()
                     * and wipe shared localStorage.
                     */
                    if (this.#tokenService.sessionInvalidated()) {
                      this.#tokenSubject$.next(null);
                      this.#isRefreshingToken = false;
                      return throwError(() => innerErr);
                    }

                    this.#store.dispatch(authActions.refreshTokenFail(
                      innerErr,
                      refreshTokenAction
                    ));

                    this.#tokenSubject$.next(null);
                    this.#isRefreshingToken = false;
                    this.#refreshFailed = true;
                    this.#refreshFailedForToken = _tokenData?.accessToken ?? null;

                    /**
                     * Re-throw the ORIGINAL request's 401, not the refresh call's
                     * failure — same as the requests that only waited on the refresh
                     * (the `else` branch's `throwError(() => err)`). Otherwise the
                     * initiating request's *Fail action (e.g. loadUserFail) carries
                     * the refresh error's non-401 status, which errors.effect does
                     * NOT blacklist, so it gets promoted to the "Couldn't load vital
                     * parts" dialog over the login screen. The refresh error is kept
                     * on refreshTokenFail above (redirect + logging + the toast).
                     */
                    return throwError(() => err);
                  }),
                  switchMap((tokenData) => {
                    /**
                     * Another tab invalidated the session while refresh was in-flight - discard the result to avoid overwriting
                     * the new user's token in localStorage.
                     */
                    if (this.#tokenService.sessionInvalidated()) {
                      return throwError(() => err);
                    }

                    this.#tokenSubject$.next(tokenData.accessToken);
                    this.#refreshFailed = false;
                    this.#refreshFailedForToken = null;

                    this.#store.dispatch(authActions.refreshTokenSuccess(
                      tokenData,
                      refreshTokenAction
                    ));

                    this.#tokenService.setToken(tokenData);

                    return next.handle(this.#addToken(req, tokenData.accessToken));
                  }),
                  finalize(() => {
                    this.#activeRequests = this.#activeRequests - 1;

                    if (this.#activeRequests === 0) {
                      this.#isRefreshingToken = false;
                    }
                  })
                );
            } else {
              return this.#tokenSubject$.pipe(
                filter((token) => token !== undefined),
                take(1),
                switchMap((token) => {
                  if (token === null) {
                    return throwError(() => err);
                  }
                  return next.handle(this.#addToken(req, token));
                }),
                finalize(() => {
                  this.#activeRequests = this.#activeRequests - 1;

                  if (this.#activeRequests === 0) {
                    this.#isRefreshingToken = false;
                  }
                })
              );
            }
          } else {
            return throwError(() => err);
          }
        })
      );
  }

  #addToken(req: HttpRequest<any>, token: string) {
    if (!req.headers.get('zefSkipAuthHeader')) {
      if (!token) {
        return req;
      }
      return req.clone({ setHeaders: { Authorization: 'Bearer ' + token }});
    }

    const newHeaders = req.headers.delete('zefSkipAuthHeader')
    return req.clone({ headers: newHeaders });
  }
}

export const authTokenInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthAndRefreshTokenInterceptor,
  multi: true
};
